import { ReadonlyVec2, ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import {
    MouseEvent as ReactMouseEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { editBlockedMessageId } from '~/components/Structures/editBlockedMessage';
import type { ContourEditBlocker } from '~/domain/dicom/contourEditBlocker';
import {
    domainEventBus,
    RoiPointCoordinatesChangeRequestedEvent,
} from '~/domain/events';
import { toPointMm } from '~/domain/geometry/patientGeometry';
import { MIN_DRAG_CANVAS_PIXELS } from '~/engine/view/picking/curveSelectionBox';
import { displayedPlanePlacement } from '~/engine/view/planeViewGeometry';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import { useWindowMouseDrag } from '~/hooks/useWindowMouseDrag';
import type { UncommittedPointCoordinates } from '~/state/atoms/structureSet/roiAtoms';
import { enqueueSnackbar } from '~/utils/notifications';

/** Radius of the transparent drag hit area, in px. */
const HANDLE_RADIUS_PX = 10;
/** Drag ghost marker geometry, in px. */
const GHOST_RADIUS_PX = 6;
const GHOST_ARM_PX = 4;

interface TilePoiInteractionProps {
    view: PlaneView;
    /** The active point ROI number (the resolver's selection). */
    selectedRoiNumber: number;
    /** A point-type ROI is selected with no coordinates yet → click to place. */
    isReadyToPlace: boolean;
    /**
     * The placed point may be dragged: the poi-move tool is armed and the
     * point ROI is visible. The handle sits at the point's projection onto
     * the tile plane, and only while the point lies in the displayed slice.
     */
    isMovable: boolean;
    /** The selected point's patient coordinates (mm), or null when unplaced. */
    selectedPointMm: ReadonlyVec3 | null;
    /** The guard that refuses a change to the point, if any. */
    editBlocker: ContourEditBlocker | undefined;
    /**
     * Receives where the drag in progress would place the point if released
     * now, and null when no drag is in progress or the release would leave
     * the point as it is.
     */
    onReleasePreview: (preview: UncommittedPointCoordinates | null) => void;
}

interface DragStart {
    client: vec2;
    mm: vec3;
}

function publishCoordinates(roiNumber: number, mm: ReadonlyVec3): void {
    void domainEventBus.publish(
        new RoiPointCoordinatesChangeRequestedEvent(
            roiNumber,
            { x: mm[0], y: mm[1], z: mm[2] },
            'user'
        )
    );
}

/**
 * True when a pointer displacement is a click rather than a drag: under
 * {@link MIN_DRAG_CANVAS_PIXELS} on both axes, so hand jitter moves nothing.
 */
function isClickDisplacement(dx: number, dy: number): boolean {
    return (
        Math.abs(dx) < MIN_DRAG_CANVAS_PIXELS &&
        Math.abs(dy) < MIN_DRAG_CANVAS_PIXELS
    );
}

/**
 * The patient mm coordinates a drag from `start` places the point at if
 * released at `client`, or null when the release leaves the point as it is:
 * a click, jitter included, or a release on a slice the point is not in.
 */
function releaseCoordinatesMm(
    view: PlaneView,
    start: DragStart,
    client: ReadonlyVec2
): vec3 | null {
    if (
        isClickDisplacement(
            client[0] - start.client[0],
            client[1] - start.client[1]
        ) ||
        displayedPlanePlacement(toPointMm(start.mm), view) === 'offSlice'
    ) {
        return null;
    }
    const delta = vec3.subtract(
        vec3.create(),
        view.clientToPatientCoordinates(client),
        view.clientToPatientCoordinates(start.client)
    );
    return vec3.add(vec3.create(), start.mm, delta);
}

/**
 * Whether a change to the point is refused; when it is, tells the user which
 * guard refuses it (approval outranks the lock).
 */
function refuseBlockedEdit(blocker: ContourEditBlocker | undefined): boolean {
    if (!blocker) {
        return false;
    }
    enqueueSnackbar(<FormattedMessage id={editBlockedMessageId(blocker)} />, {
        variant: 'error',
    });
    return true;
}

/**
 * Interactive point-of-interest layer for an EDITABLE grid tile (its
 * Environment is the active edit target and it draws that Environment's
 * primary image). Click-to-place an unplaced point ROI, and drag its marker
 * to move it; both publish `RoiPointCoordinatesChangeRequestedEvent` for the
 * active selected ROI. The drag moves the point by the patient-space delta
 * of the two cursor positions in the tile plane, so the point keeps its
 * depth along the tile normal. The handle is offered only while the point
 * lies in the displayed slice, so a point is never moved on a slice it is
 * not in. A release that moved the pointer less than a drag, or that comes
 * after the view scrolled off the point's slice, leaves the point as it is.
 * While the drag is in progress, `onReleasePreview` receives where a release
 * would place the point, kept current through scrolls and zooms.
 * A point ROI that is approved or locked is refused at the press, with the
 * guard named, and nothing is published. The marker's pointer-events hit
 * area is what keeps the press off the canvas, so the armed tool never pans
 * under a drag (the engine dispatches only events whose target is the tile
 * canvas).
 */
export default function TilePoiInteraction({
    view,
    selectedRoiNumber,
    isReadyToPlace,
    isMovable,
    selectedPointMm,
    editBlocker,
    onReleasePreview,
}: TilePoiInteractionProps) {
    const startRef = useRef<DragStart | null>(null);
    const [pointer, setPointer] = useState<vec2 | null>(null);

    // Runs after every render: the mount re-renders this layer on each
    // scroll and zoom, and each pointer move sets `pointer`; any of them can
    // move where the release lands.
    useEffect(() => {
        const start = startRef.current;
        if (!start || !pointer) {
            return;
        }
        const coordinatesMm = releaseCoordinatesMm(view, start, pointer);
        onReleasePreview(
            coordinatesMm && { roiNumber: selectedRoiNumber, coordinatesMm }
        );
    });

    // A drag that ends without onEnd (the layer unmounts mid-drag) must not
    // leave its preview behind.
    useEffect(
        () => () => {
            if (startRef.current) {
                onReleasePreview(null);
            }
        },
        [onReleasePreview]
    );

    const onDrag = useCallback((event: MouseEvent) => {
        if (startRef.current) {
            setPointer(vec2.fromValues(event.clientX, event.clientY));
        }
    }, []);

    const onEnd = useCallback(
        (event: MouseEvent) => {
            const start = startRef.current;
            startRef.current = null;
            setPointer(null);
            onReleasePreview(null);
            if (!start) {
                return;
            }
            const coordinatesMm = releaseCoordinatesMm(
                view,
                start,
                vec2.fromValues(event.clientX, event.clientY)
            );
            if (coordinatesMm) {
                publishCoordinates(selectedRoiNumber, coordinatesMm);
            }
        },
        [view, selectedRoiNumber, onReleasePreview]
    );

    const startDrag = useWindowMouseDrag(onDrag, onEnd);

    const beginDrag = (event: ReactMouseEvent<SVGElement>): void => {
        if (!selectedPointMm || refuseBlockedEdit(editBlocker)) {
            return;
        }
        startRef.current = {
            client: vec2.fromValues(event.clientX, event.clientY),
            mm: vec3.fromValues(
                selectedPointMm[0],
                selectedPointMm[1],
                selectedPointMm[2]
            ),
        };
        startDrag(event);
    };

    const handlePlace = (event: ReactMouseEvent<SVGRectElement>): void => {
        if (selectedRoiNumber === -1 || refuseBlockedEdit(editBlocker)) {
            return;
        }
        publishCoordinates(
            selectedRoiNumber,
            view.clientToPatientCoordinates(
                vec2.fromValues(event.clientX, event.clientY)
            )
        );
    };

    const marker =
        isMovable &&
        selectedPointMm &&
        displayedPlanePlacement(toPointMm(selectedPointMm), view) !== 'offSlice'
            ? view.canvasCoordsFromPatientCoords(selectedPointMm)
            : null;

    if (!isReadyToPlace && !marker) {
        return null;
    }

    const start = startRef.current;
    const dx = start && pointer ? pointer[0] - start.client[0] : 0;
    const dy = start && pointer ? pointer[1] - start.client[1] : 0;
    // The ghost stays put while the drop would still count as a click, so
    // it never shows a move that the release then discards.
    const dragging = start !== null && !isClickDisplacement(dx, dy);
    const displayX = (marker?.[0] ?? 0) + (dragging ? dx : 0);
    const displayY = (marker?.[1] ?? 0) + (dragging ? dy : 0);

    return (
        <svg
            data-testid="tile-poi-interaction"
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 8,
            }}
        >
            {isReadyToPlace ? (
                <rect
                    data-testid="poi-place-target"
                    x={0}
                    y={0}
                    width="100%"
                    height="100%"
                    fill="transparent"
                    style={{ pointerEvents: 'auto', cursor: 'crosshair' }}
                    onClick={handlePlace}
                />
            ) : null}
            {marker ? (
                <g>
                    <circle
                        data-testid="poi-move-handle"
                        cx={displayX}
                        cy={displayY}
                        r={HANDLE_RADIUS_PX}
                        fill="transparent"
                        style={{
                            pointerEvents: 'auto',
                            cursor: dragging ? 'grabbing' : 'move',
                        }}
                        onMouseDown={beginDrag}
                    />
                    {dragging ? (
                        <g style={{ pointerEvents: 'none' }}>
                            <circle
                                cx={displayX}
                                cy={displayY}
                                r={GHOST_RADIUS_PX}
                                fill="none"
                                stroke="#ffffff"
                                strokeWidth={1.5}
                                strokeDasharray="2 2"
                            />
                            <line
                                x1={displayX - GHOST_ARM_PX}
                                y1={displayY}
                                x2={displayX + GHOST_ARM_PX}
                                y2={displayY}
                                stroke="#ffffff"
                                strokeWidth={1.5}
                            />
                            <line
                                x1={displayX}
                                y1={displayY - GHOST_ARM_PX}
                                x2={displayX}
                                y2={displayY + GHOST_ARM_PX}
                                stroke="#ffffff"
                                strokeWidth={1.5}
                            />
                        </g>
                    ) : null}
                </g>
            ) : null}
        </svg>
    );
}
